import React, { useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  useTheme, 
  useMediaQuery,
  Fade,
  Slide,
  Grow,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowForward, 
  Star, 
  TrendingUp, 
  CheckCircle,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const ModernSectionBlock = ({
  children,
  bgcolor = 'background.paper',
  maxWidth = 'lg',
  py = { xs: 8, md: 12 },
  px = { xs: 2, md: 6, lg: 8, xl: 12 },
  title,
  subtitle,
  description,
  titleColor,
  subtitleColor,
  descriptionColor,
  align = 'center', // 'center' | 'left' | 'right'
  variant = 'default', // 'default' | 'hero' | 'feature' | 'testimonial' | 'cta' | 'stats'
  backgroundImage,
  backgroundVideo,
  videoFallbackBg = 'primary.main', // Fallback background color when video fails
  enableMobileVideo = true, // Enable video on mobile devices
  overlay = false,
  overlayOpacity = 0.1,
  animation = 'fade', // 'fade' | 'slide' | 'grow' | 'none'
  animationDelay = 0,
  showDivider = false,
  dividerVariant = 'fullWidth',
  showBadge = false,
  badgeText,
  badgeColor = 'primary',
  showActionButton = false,
  actionButtonText,
  actionButtonIcon,
  onActionClick,
  elevation = 0,
  borderRadius = 0,
  gradient = false,
  gradientColors = ['gold.main', 'accent.main'],
  gradientDirection = 'to right',
  childrenContainerProps = {},
  ...rest
}) => {
  const theme = useTheme();
  
  // Mobile device detection
  const isMobileDevice = React.useMemo(() => {
    if (typeof navigator !== 'undefined') {
      return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
    return false;
  }, []);
  
  // Video fallback state management - simplified approach
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  // Video event handlers - only fallback on actual errors
  const handleVideoError = (e) => {
    console.warn('Video failed to load, showing fallback:', e);
    setVideoError(true);
  };

  // Animation variants
  const animationVariants = {
    fade: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.6, delay: animationDelay }
    },
    slide: {
      initial: { opacity: 0, x: -50 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.8, delay: animationDelay }
    },
    grow: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.6, delay: animationDelay }
    }
  };

  // Get variant-specific styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'hero':
        return {
          minHeight: { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 80px)', md: 'calc(100vh - 60px)' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          position: 'relative',
          overflow: 'visible',
          py: { xs: 4, sm: 6, md: 8 }
        };
      case 'feature':
        return {
          py: { xs: 10, md: 16 },
          textAlign: align,
          position: 'relative'
        };
      case 'testimonial':
        return {
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          bgcolor: 'secondary.light'
        };
      case 'cta':
        return {
          py: { xs: 10, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        };
      case 'stats':
        return {
          py: { xs: 6, md: 10 },
          textAlign: 'center'
        };
      default:
        return {
          py,
          textAlign: align
        };
    }
  };

  // Get title variant based on section type
  const getTitleVariant = () => {
    switch (variant) {
      case 'hero':
        return 'h1';
      case 'feature':
        return 'h2';
      case 'testimonial':
        return 'h3';
      case 'cta':
        return 'h1';
      case 'stats':
        return 'h3';
      default:
        return 'h3';
    }
  };

  const MotionBox = motion(Box);

  return (
    <MotionBox
      component="section"
      sx={{
        bgcolor: videoError && backgroundVideo ? videoFallbackBg : bgcolor,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        ...getVariantStyles(),
        ...rest.sx,
      }}
      initial={animation !== 'none' ? animationVariants[animation]?.initial : false}
      animate={animation !== 'none' ? animationVariants[animation]?.animate : false}
      transition={animation !== 'none' ? animationVariants[animation]?.transition : {}}
    >
      {/* Background Image/Video */}
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
        />
      )}

      {backgroundVideo && !videoError && (enableMobileVideo || !isMobileDevice) && (
        <Box
          component="video"
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onError={handleVideoError}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={backgroundVideo} type="video/webm" />
          <source src={backgroundVideo.replace('.webm', '.mp4')} type="video/mp4" />
        </Box>
      )}

      {/* Gradient Overlay */}
      {gradient && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(${gradientDirection}, ${gradientColors.map(color => {
              // Handle nested palette colors like 'gold.main'
              const colorParts = color.split('.');
              if (colorParts.length === 2 && theme.palette[colorParts[0]]) {
                return theme.palette[colorParts[0]][colorParts[1]] || color;
              }
              return theme.palette[color] || color;
            }).join(', ')})`,
            zIndex: 1,
          }}
        />
      )}

      {/* Content Overlay */}
      {overlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            opacity: overlayOpacity,
            zIndex: 2,
          }}
        />
      )}

      {/* Content Container */}
      <Box 
        sx={{ 
          position: 'relative',
          zIndex: 3,
          px,
          width: '100%',
          ...childrenContainerProps
        }}
      >
        {/* Badge */}
        {showBadge && badgeText && (
          <Box sx={{ mb: 3, textAlign: align }}>
            <Chip
              label={badgeText}
              color={badgeColor}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 2,
                py: 0.5,
              }}
            />
          </Box>
        )}

        {/* Title and Subtitle */}
        {(title || subtitle || description) && (
          <Box 
            mb={variant === 'hero' ? 6 : 4} 
            textAlign={align}
            sx={{
              maxWidth: variant === 'hero' ? { xs: 600, md: 800, lg: 1000 } : { xs: 500, md: 600, lg: 800 },
              mx: align === 'center' ? 'auto' : undefined,
              ...(backgroundVideo && {
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                backdropFilter: 'blur(4px)',
              }),
            }}
          >
            {title && (
              <Typography
                variant={getTitleVariant()}
                fontWeight={variant === 'hero' ? 800 : 700}
                color={
                  titleColor || (
                    variant === 'hero' ? 'gold.main' :
                    variant === 'stats' ? 'primary.contrastText' :
                    'text.primary'
                  )
                }
                gutterBottom
                sx={{
                  fontSize: {
                    xs: variant === 'hero' ? '2.5rem' : '2rem',
                    md: variant === 'hero' ? '4rem' : '2.5rem',
                    lg: variant === 'hero' ? '5rem' : '3rem',
                  },
                  lineHeight: 1.2,
                  mb: 2,
                  ...(variant === 'stats' && {
                    textShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  }),
                }}
              >
                {title}
              </Typography>
            )}
            
            {subtitle && (
              <Typography
                variant={variant === 'hero' ? 'h5' : 'h6'}
                color={
                  subtitleColor || (
                    variant === 'stats' ? 'gold.contrastText' : 'text.secondary'
                  )
                }
                gutterBottom
                sx={{
                  fontSize: {
                    xs: variant === 'hero' ? '1.25rem' : '1.125rem',
                    md: variant === 'hero' ? '1.5rem' : '1.25rem',
                  },
                  fontWeight: 400,
                  mb: description ? 2 : 0,
                }}
              >
                {subtitle}
              </Typography>
            )}

            {description && (
              <Typography
                variant="body1"
                color={descriptionColor || 'text.secondary'}
                sx={{
                  fontSize: {
                    xs: '1rem',
                    md: '1.125rem',
                  },
                  lineHeight: 1.7,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        )}

        {/* Divider */}
        {showDivider && (
          <Box sx={{ mb: 4 }}>
            <Divider 
              variant={dividerVariant}
              sx={{
                borderColor: 'divider',
                opacity: 0.3,
              }}
            />
          </Box>
        )}

        {/* Children Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 4,
          }}
        >
          {children}
        </Box>

        {/* Action Button */}
        {showActionButton && actionButtonText && (
          <Box 
            sx={{ 
              mt: 4, 
              textAlign: align,
              display: 'flex',
              justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
            }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Tooltip title={actionButtonText}>
                <IconButton
                  onClick={onActionClick}
                  sx={{
                    bgcolor: 'gold.main',
                    color: 'gold.contrastText',
                    p: 2,
                    '&:hover': {
                      bgcolor: 'gold.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[8],
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {actionButtonIcon || <ArrowForward />}
                </IconButton>
              </Tooltip>
            </motion.div>
          </Box>
        )}
      </Box>
    </MotionBox>
  );
};

export default ModernSectionBlock; 